import React from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import { PlayerControl, PlayerIcon, PlayingAsset, PropTypesAdditional, PlayingMode, PropTypesPlayerUICommon } from 'types/Player';
import { useAssetReplayCapable } from 'hooks/useAssetReplayCapable/useAssetReplayCapable';
import { getRecordingIconName } from 'utils/fnData';
import { TimeClusterRecordingTypeMap } from 'types/RecordingTypes';
import {
    castEnabled,
    getBroadcastPlayerActionComponents,
    getBroadcastPlayerMetaData,
    getBroadcastPlayerMoreOverlayMetaData,
    isSeriesPlayerAsset,
} from 'utils/fnPlayerUI';
import { ChannelLogo, ChannelLogoFallback } from 'components/PlayerUI/PlayerUI.css';
import { Picture } from 'components/Picture/Picture';
import { isCatchupPlayerAsset } from 'utils/fnTypeGuards';

export const useCatchupPlayerUI = (common: PropTypesPlayerUICommon, asset: PlayingAsset): PropTypesAdditional => {
    const { isReplayCapable } = useAssetReplayCapable(asset);

    if (!isCatchupPlayerAsset(asset)) {
        return null;
    }

    const { channelLogo, channelName, isRecorded, title, subtitle, logicalChannelNumber, description, cast, originalLanguages } = asset;

    const getIcons = (): PlayerIcon[] => {
        const metaIcons: PlayerIcon[] = [];

        if (isReplayCapable) {
            metaIcons.push(PlayerIcon.CATCHUP);
        }

        if (isRecorded) {
            metaIcons.push(getRecordingIconName(TimeClusterRecordingTypeMap.PAST) as PlayerIcon);
        }

        return metaIcons;
    };

    const getControls = (): PlayerControl[] => {
        const controls: PlayerControl[] = [PlayerControl.FULL_SCREEN, PlayerControl.VOLUME];
        const isBroadcastLive = asset.endTime ? new Date(asset.endTime) > new Date() : false;

        if (castEnabled()) {
            controls.push(PlayerControl.CAST);
        }

        controls.push(PlayerControl.INFO);

        controls.push(PlayerControl.SUBTITLE);

        if (isBroadcastLive) {
            controls.push(PlayerControl.GO_TO_LIVE);
        }

        if (!isMobile && !isTablet) {
            controls.push(PlayerControl.JUMP_BACK);
            controls.push(PlayerControl.JUMP_FORWARD);
        }
        controls.push(PlayerControl.PLAY_PAUSE);

        if (common.playingMode === PlayingMode.TIME_SHIFT) {
            controls.push(PlayerControl.GO_TO_LIVE);
        }

        if (isSeriesPlayerAsset(asset)) {
            controls.push(PlayerControl.EPISODES);
            controls.push(PlayerControl.NEXT_ASSET);
        }

        return controls;
    };

    const controls = getControls();

    const { actionPopup, mobileOverlayActionHandlers } = getBroadcastPlayerActionComponents(controls);

    return {
        logo: (channelLogo || channelName) && (
            <ChannelLogo>
                <Picture src={channelLogo} hPosition={'left'} vPosition={'center'} fit={'scale-down'}>
                    {channelName && <ChannelLogoFallback>{channelName}</ChannelLogoFallback>}
                </Picture>
            </ChannelLogo>
        ),
        logicalChannelNumber,
        metaData: getBroadcastPlayerMetaData(asset),
        contentMarker: null,
        metaIcons: getIcons(),
        disabledControls: [],
        controls,
        seekEnabled: true,
        title,
        subtitle,
        actionPopup,
        mobileOverlayActionHandlers,
        moreOverlayMetaData: getBroadcastPlayerMoreOverlayMetaData(asset),
        description,
        cast,
        originalLanguages,
    };
};
